import { Link } from "react-router-dom";
import {
  Inbox,
  Headphones,
  Wrench,
  Briefcase,
  MessageCircle,
  ShieldAlert,
  Globe,
  Scale,
  AlertTriangle,
  CreditCard,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";
import type { PulseInboxBucket } from "./types";

const ICON_MAP: Record<PulseInboxBucket["icon"], LucideIcon> = {
  inbox: Inbox,
  headphones: Headphones,
  wrench: Wrench,
  briefcase: Briefcase,
  "message-circle": MessageCircle,
  "shield-alert": ShieldAlert,
  globe: Globe,
  scale: Scale,
  "alert-triangle": AlertTriangle,
  "credit-card": CreditCard,
};

export function PulseInboxCard({ bucket }: { bucket: PulseInboxBucket }) {
  const Icon = ICON_MAP[bucket.icon] ?? Inbox;
  const empty = bucket.count === 0;
  const severity = bucket.severity ?? "normal";

  return (
    <Link
      to={bucket.to}
      className={cn(
        "group relative flex flex-col gap-2 p-4 rounded-lg border transition-all duration-150 ease-nx-out",
        empty
          ? "bg-bg-card border-border-subtle hover:border-border opacity-65 hover:opacity-100"
          : "bg-bg-card border-border hover:border-border-strong hover:bg-bg-hover hover:-translate-y-px hover:shadow-nx-md",
      )}
    >
      <div className="flex items-start justify-between">
        <div
          className={cn(
            "w-9 h-9 rounded-md flex items-center justify-center",
            severity === "critical" && !empty
              ? "bg-danger-soft text-danger"
              : severity === "warning" && !empty
              ? "bg-warning-soft text-warning"
              : empty
              ? "bg-bg-subtle text-fg-subtle"
              : "bg-accent-soft text-accent",
          )}
        >
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <ArrowRight
          size={14}
          className="text-fg-subtle opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </div>
      <div>
        <div
          className={cn(
            "text-2xl font-bold tracking-tight tabular-nums",
            empty ? "text-fg-subtle" : "text-fg",
          )}
        >
          {bucket.count}
        </div>
        <div className="text-xs text-fg-muted leading-snug">{bucket.label}</div>
      </div>
    </Link>
  );
}
